import React from "react";

import { css, cx } from "@emotion/css";
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { PractitionerBilling } from "@/models/PractitionerBillingModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import {
  FileNameColumn,
  FileNameColumnHeader,
} from "@/uiKit/molecules/PractitionerBillingTable/columns/FileNameColumn.tsx";
import { PeriodColumn, PeriodColumnHeader } from "@/uiKit/molecules/PractitionerBillingTable/columns/PeriodColumn.tsx";
import { StatusColumn, StatusColumnHeader } from "@/uiKit/molecules/PractitionerBillingTable/columns/StatusColumn.tsx";
import {
  UpdatedAtColumn,
  UpdatedAtColumnHeader,
} from "@/uiKit/molecules/PractitionerBillingTable/columns/UpdatedAtColumn.tsx";
import { MuiTable } from "@/uiKit/Table.tsx";
import { HoveredRowFeature } from "@/utils/table/RowHoverFeature.ts";

export type PractitionerBillingTableProps = {
  className?: string;
};

const columnHelper = createColumnHelper<PractitionerBilling>();

const FILE_NAME_COLUMN_SIZE = 45;
const PERIOD_COLUMN_SIZE = 20;
const UPDATED_AT_COLUMN_SIZE = 20;
const STATUS_COLUMN_SIZE = 15;

const columnDefinitions = {
  filename: columnHelper.accessor("fileName", {
    cell: FileNameColumn,
    header: FileNameColumnHeader,
    size: FILE_NAME_COLUMN_SIZE,
  }),
  period: columnHelper.accessor("period", {
    cell: PeriodColumn,
    header: PeriodColumnHeader,
    size: PERIOD_COLUMN_SIZE,
  }),
  updatedAt: columnHelper.accessor("fileUploadDate", {
    cell: UpdatedAtColumn,
    header: UpdatedAtColumnHeader,
    size: UPDATED_AT_COLUMN_SIZE,
  }),
  status: columnHelper.accessor("status", {
    cell: StatusColumn,
    header: StatusColumnHeader,
    size: STATUS_COLUMN_SIZE,
  }),
};

export const PractitionerBillingTable: React.FC<PractitionerBillingTableProps> = ({ className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  const practitionerBilling = Queries.billing.usePractitionerBillingFiles({});

  const table = useReactTable<PractitionerBilling>({
    _features: [HoveredRowFeature],
    data: practitionerBilling,
    getRowId: row => row.id,
    defaultColumn: {
      size: 0,
      minSize: 0,
      maxSize: 100,
    },
    columns: Object.values(columnDefinitions),
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
  });

  if (practitionerBilling.length === 0) {
    return <Typography>{t("pages.practitionerBilling.noData")}</Typography>;
  }

  return (
    <MuiTable
      id="practitioner-billing-table"
      table={table}
      className={cx(styles.container, className)}
      classes={{
        row: "practitioner-billing-row",
        cell: styles.cell,
      }}
    />
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  cell: css({
    padding: theme.spacing(2, 0),
    height: theme.spacing(22),
  }),
});
